import * as React from 'react';
import { route } from '../../utils/route';
import type { CountryInfo } from '../../types/barrios-landing';

interface CountriesSliderProps {
  countries: CountryInfo[];
}

export function CountriesSlider({ countries }: CountriesSliderProps) {
  if (countries.length === 0) return null;

  const scrollRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const el = scrollRef.current;
    if (!el || countries.length <= 3) return;

    let animId: number;
    let scrollPos = 0;
    const speed = 0.5; // px per frame

    const step = () => {
      scrollPos += speed;
      if (scrollPos >= el.scrollWidth / 2) {
        scrollPos = 0;
      }
      el.scrollLeft = scrollPos;
      animId = requestAnimationFrame(step);
    };

    // Pause on hover
    const pause = () => cancelAnimationFrame(animId);
    const resume = () => { animId = requestAnimationFrame(step); };

    animId = requestAnimationFrame(step);
    el.addEventListener('mouseenter', pause);
    el.addEventListener('mouseleave', resume);

    return () => {
      cancelAnimationFrame(animId);
      el.removeEventListener('mouseenter', pause);
      el.removeEventListener('mouseleave', resume);
    };
  }, [countries.length]);

  // Duplicate for infinite scroll effect
  const items = countries.length > 3 ? [...countries, ...countries] : countries;

  return (
    <section className="bp-countries">
      <style>{`
        .bp-countries {
          padding: 64px 24px;
          background: #fafaf7;
          overflow: hidden;
        }
        .bp-countries-inner {
          max-width: 960px;
          margin: 0 auto;
        }
        .bp-countries h2 {
          text-align: center;
          font-size: 26px;
          font-weight: 700;
          color: #1a1a1a;
          margin: 0 0 8px;
        }
        .bp-countries .section-sub {
          text-align: center;
          font-size: 16px;
          color: #666;
          margin: 0 0 32px;
        }
        .bp-countries-scroll {
          display: flex;
          gap: 16px;
          overflow-x: auto;
          scroll-behavior: auto;
          -ms-overflow-style: none;
          scrollbar-width: none;
          padding: 4px 0;
        }
        .bp-countries-scroll::-webkit-scrollbar {
          display: none;
        }
        .bp-country-card {
          flex: 0 0 220px;
          background: #fff;
          border-radius: 12px;
          border: 1px solid #eee;
          padding: 20px;
          transition: box-shadow 0.2s, border-color 0.2s;
          text-decoration: none;
          display: block;
        }
        a.bp-country-card:hover {
          box-shadow: 0 4px 12px rgba(0,0,0,0.06);
          border-color: #1EAA25;
          text-decoration: none;
        }
        .bp-country-name {
          font-size: 15px;
          font-weight: 700;
          color: #1a1a1a;
          margin: 0 0 8px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .bp-country-orders {
          font-size: 13px;
          color: #666;
          margin: 0 0 10px;
        }
        .bp-country-delivery {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 13px;
          color: #1EAA25;
          font-weight: 500;
        }
        .bp-country-delivery i {
          font-size: 12px;
        }
        .bp-country-discount {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          font-size: 13px;
          font-weight: 600;
          color: #b45309;
          background: #fef3c7;
          border-radius: 6px;
          padding: 3px 8px;
          margin-bottom: 8px;
        }
        .bp-country-discount i {
          font-size: 11px;
        }
        .bp-country-discount-link {
          color: #92400e;
          font-size: 12px;
          text-decoration: none;
          margin-left: 2px;
        }
        .bp-country-discount-link:hover {
          text-decoration: underline;
        }
        @media (max-width: 640px) {
          .bp-countries h2 {
            font-size: 22px;
          }
          .bp-country-card {
            flex: 0 0 180px;
            padding: 16px;
          }
        }
      `}</style>
      <div className="bp-countries-inner">
        <h2>Barrios donde ya entregamos</h2>
        <p className="section-sub">Estos son algunos de los countries y barrios privados que nos eligen.</p>
        <div className="bp-countries-scroll" ref={scrollRef}>
          {items.map((country, i) => {
            const Tag = country.slug ? 'a' : 'div';
            const linkProps = country.slug ? { href: `/barrios-privados/${country.slug}` } : {};
            return (
              <Tag key={`${country.name}-${i}`} className="bp-country-card" {...linkProps}>
                <div className="bp-country-name">{country.name}</div>
                {country.discount && (
                  <div className="bp-country-discount">
                    <i className="fa fa-tag" />
                    {country.discount.discount}% OFF
                    <a href={route('discount.share', { code: country.discount.code })} className="bp-country-discount-link" onClick={(e) => e.stopPropagation()}>
                      Aplicar →
                    </a>
                  </div>
                )}
                {!!country.total_kilos && (
                  <div className="bp-country-orders">
                    {country.total_kilos.toLocaleString('es-AR')} kg entregados
                  </div>
                )}
                {country.next_delivery && (
                  <div className="bp-country-delivery">
                    <i className="fa fa-truck" />
                    Recibilo {country.next_delivery.day_name} {country.next_delivery.shift}
                  </div>
                )}
              </Tag>
            );
          })}
        </div>
      </div>
    </section>
  );
}
